/**
 * Activity-feed assembly for the Overview tab. Normalises three heterogeneous
 * logs — COGS push log, SIT reclass log, chart-of-accounts/tax sync log — into
 * one chronologically-sorted, day-grouped stream the feed renders read-only.
 */
import type { RouterOutputs } from '@/lib/trpc';
import { dayKey } from './format';

type CogsLog = RouterOutputs['xero']['getCogsLog'];
type SitLog = RouterOutputs['xero']['getSitLog'];
type SyncLogs = RouterOutputs['xero']['getSyncLogs'];

export type ActivityKind = 'cogs' | 'sit' | 'sync';

export interface ActivityEntry {
  id: string;
  kind: ActivityKind;
  /** ISO timestamp used for sorting + day grouping. */
  at: string;
  description: string;
  /** AUD amount when the entry carries one; null for sync entries. */
  amount: number | null;
  journalId: string | null;
  journalNumber: string | null;
  /** success | failed | pending | skipped_locked_period | completed | … */
  status: string;
  isReversal?: boolean;
  errorMessage?: string | null;
}

export interface ActivityDay {
  key: string;
  /** Representative ISO timestamp for the day (first entry). */
  at: string;
  entries: ActivityEntry[];
}

/** Map the heterogeneous sync-log status vocabulary onto the journal one so the
 *  feed's status chip renders consistently. */
const normaliseSyncStatus = (status: string): string => {
  if (status === 'completed') return 'success';
  if (status === 'failed') return 'failed';
  return 'pending';
};

export function buildActivityFeed(
  cogs: CogsLog | undefined,
  sit: SitLog | undefined,
  sync: SyncLogs | undefined
): ActivityEntry[] {
  const entries: ActivityEntry[] = [];

  for (const row of cogs?.data ?? []) {
    const at = row.pushed_at ?? row.created_at ?? null;
    if (!at) continue;
    entries.push({
      id: `cogs-${row.id}`,
      kind: 'cogs',
      at,
      description: row.sku ? `COGS journal · ${row.sku}` : 'COGS journal',
      amount: row.amount != null ? Number(row.amount) : null,
      journalId: row.xero_journal_id,
      journalNumber: row.journal_number,
      status: row.status,
      errorMessage: row.error_message,
    });
  }

  for (const row of sit?.data ?? []) {
    const at = row.pushed_at ?? row.created_at ?? null;
    if (!at) continue;
    const poLabel = row.po_number ? ` · ${row.po_number}` : '';
    entries.push({
      id: `sit-${row.id}`,
      kind: 'sit',
      at,
      description: `Stock-in-transit reclass${poLabel}`,
      amount: row.amount_aud != null ? Number(row.amount_aud) : null,
      journalId: row.xero_journal_id,
      journalNumber: row.journal_number,
      status: row.status,
      isReversal: Boolean(row.is_reversal),
      errorMessage: row.error_message,
    });
  }

  for (const row of sync ?? []) {
    entries.push({
      id: `sync-${row.id}`,
      kind: 'sync',
      at: row.started_at,
      description: `${row.sync_type.replace(/_/g, ' ')} sync · ${row.items_synced ?? 0} item${(row.items_synced ?? 0) === 1 ? '' : 's'}`,
      amount: null,
      journalId: null,
      journalNumber: null,
      status: normaliseSyncStatus(row.status),
    });
  }

  // Newest first.
  return entries.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
}

export function groupByDay(entries: ActivityEntry[]): ActivityDay[] {
  const days: ActivityDay[] = [];
  const index = new Map<string, ActivityDay>();
  for (const entry of entries) {
    const key = dayKey(entry.at);
    let day = index.get(key);
    if (!day) {
      day = { key, at: entry.at, entries: [] };
      index.set(key, day);
      days.push(day);
    }
    day.entries.push(entry);
  }
  return days;
}

/** Sum of posted (success) journal amounts — drives the "Posted to Xero" KPI. */
export function sumPostedAmount(entries: ActivityEntry[]): number {
  return entries.reduce(
    (total, entry) =>
      entry.status === 'success' && entry.amount != null ? total + entry.amount : total,
    0
  );
}
